import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';

/**
 * WizardShell - Minimal layout for wizard flows
 */
export default function WizardShell({ 
  children, 
  title,
  subtitle,
  step, 
  totalSteps,
  onClose,
  showProgress = true,
}) {
  const { t } = useTranslation();
  const progress = totalSteps > 0 ? (step / totalSteps) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#F7F3EE]">
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-[700px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <Link 
              to="/panel" 
              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t('kennel.goToPanel', 'Wróć do panelu')}</span>
            </Link>
            <div className="flex items-center gap-4">
              {showProgress && (
                <span className="text-sm text-zinc-500">{step} / {totalSteps}</span>
              )}
              {onClose && (
                <button onClick={onClose} className="p-1 text-zinc-400 hover:text-zinc-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          {showProgress && <Progress value={progress} className="h-1.5 bg-zinc-100" />}
        </div>
      </header>

      <main className="py-8 px-4">
        <div className="max-w-[700px] mx-auto">
          {(title || subtitle) && (
            <div className="text-center mb-8">
              {title && <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">{title}</h1>}
              {subtitle && <p className="text-zinc-500">{subtitle}</p>}
            </div>
          )}
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden"
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
}

/**
 * WizardStep - Single step content with clean spacing
 */
export function WizardStep({
  children,
  icon: Icon,
  title,
  subtitle,
  microcopy,
  aiScoreBoost,
  footer,
}) {
  const sub = subtitle || microcopy;
  return (
    <div className="p-6 sm:p-8">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-11 h-11 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#F47A3B]" />
            </div>
          )}
          <div>
            {title && <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>}
            {sub && <p className="text-sm text-zinc-500 mt-0.5">{sub}</p>}
          </div>
        </div>
        {aiScoreBoost && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] text-xs font-medium">
            +{aiScoreBoost}% AI
          </span>
        )}
      </div>

      <div className="space-y-5 wizard-inputs">
        {children}
      </div>

      {footer && (
        <div className="mt-6 pt-4 border-t border-gray-100">{footer}</div>
      )}
    </div>
  );
}

/**
 * WizardActions - Navigation buttons (apisai-primary orange, 48px+).
 * `onSkip` (opcjonalny) — gdy podany, pod CTA pojawia się subtelny link "Pomiń ten krok".
 */
export function WizardActions({
  onBack,
  onNext,
  onSubmit,
  onSkip,
  skipLabel = 'Pomiń ten krok',
  backLabel = 'Wstecz',
  nextLabel = 'Dalej',
  submitLabel = 'Zapisz',
  isFirstStep = false,
  isLastStep = false,
  isNextDisabled = false,
  isLoading = false,
}) {
  const btnBase = "flex items-center justify-center gap-2 px-8 h-12 rounded-xl font-medium text-[15px] transition-all";
  const btnPrimary = isNextDisabled || isLoading
    ? `${btnBase} bg-zinc-200 text-zinc-400 cursor-not-allowed`
    : `${btnBase} bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white shadow-[var(--shadow-primary)]`;

  return (
    <div className="flex items-center justify-between p-5 sm:p-6 bg-zinc-50/80 border-t border-zinc-200">
      {!isFirstStep ? (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-4 h-12 text-zinc-500 hover:text-zinc-900 transition-colors rounded-xl hover:bg-zinc-100"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </button>
      ) : (
        <div />
      )}

      {isLastStep ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={isNextDisabled || isLoading}
          className={btnPrimary}
          data-testid="wizard-submit-btn"
        >
          {isLoading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Zapisuję...
            </>
          ) : (
            submitLabel
          )}
        </button>
      ) : (
        <div className="flex flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={onNext}
            disabled={isNextDisabled}
            className={btnPrimary}
            data-testid="wizard-next-btn"
          >
            {nextLabel}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="text-zinc-400 hover:text-zinc-700 text-[12.5px] font-medium transition-colors px-2"
              data-testid="wizard-skip-btn"
            >
              {skipLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
